import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { getPageType } from '@frameio/shared/routing';
import { useSetRequestVariableParams } from '../../hooks';
import {
  useQuickSearchModal,
  useTrackAdvancedSearchNavigated,
  useTrackSearchExited,
} from '../../shared/contexts';

const buttonSource = {
  ai: 'AISearch',
  standard: 'ShowAllResults',
} as const;

export const useNavigateToAdvancedSearch = () => {
  const { query, filters, closeModal, isModalOpen, scopedToProjectId } =
    useQuickSearchModal();
  const setRequestVariableParams = useSetRequestVariableParams();
  const pathname = usePathname();
  const isOnSearchPage = getPageType(pathname) === 'search';

  const trackAdvancedSearchNavigated = useTrackAdvancedSearchNavigated();
  const trackSearchExited = useTrackSearchExited();

  // @todo (SRCH-2455): Replace the legacy tab input, analytics lookup, and forwarded parameter with mode; retain navigation behavior.
  return useCallback(
    ({
      event,
      query: queryOverride,
      tab,
      highlightStart,
      highlightEnd,
    }: {
      event?: React.KeyboardEvent | React.MouseEvent;
      query?: string;
      tab?: 'standard' | 'ai';
      highlightStart?: number;
      highlightEnd?: number;
    }) => {
      trackAdvancedSearchNavigated({
        buttonSource: (tab && buttonSource[tab]) || 'ShowAllResults',
        webSource: 'Modal',
      });
      setRequestVariableParams({
        query: queryOverride ?? query,
        filters,
        projectId: scopedToProjectId,
        tab,
        highlightStart,
        highlightEnd,
      });

      // Closes the modal if it is already on search page, otherwise let search page handle the operation
      if (isOnSearchPage && isModalOpen) {
        closeModal();
        trackSearchExited({ event });
      }
    },
    [
      query,
      filters,
      scopedToProjectId,
      setRequestVariableParams,
      isOnSearchPage,
      isModalOpen,
      closeModal,
      trackAdvancedSearchNavigated,
      trackSearchExited,
    ]
  );
};
